import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CalendarDays, CreditCard, FileText, Gift, History, LogOut, PiggyBank, StickyNote } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { formatTime12 } from "../lib/data";

const STATUS = {
  booked: "Booked",
  confirmed: "Confirmed",
  cancelled: "Cancelled",
  completed: "Completed",
  active: "Active",
  paused: "Paused",
  requested: "Requested",
  pending_payment: "Awaiting payment",
  paid: "Paid",
  redeemed: "Redeemed",
};

function Section({ icon: Icon, title, count, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-lg border border-linen p-6 sm:p-7">
      <div className="flex items-center gap-3">
        <Icon size={18} className="text-moss" />
        <h2 className="font-serif text-2xl font-medium text-forest">{title}</h2>
        {count > 0 && <span className="ml-auto rounded-full bg-cream px-2.5 py-0.5 text-xs font-semibold text-forest">{count}</span>}
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-moss">{children}</p>;
}

function Pill({ status }) {
  const good = ["confirmed", "active", "paid", "completed"].includes(status);
  const bad = ["cancelled"].includes(status);
  return (
    <span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.1em] ${good ? "border-forest/30 bg-forest/10 text-forest" : bad ? "border-destructive/30 text-destructive" : "border-linen text-moss"}`}>
      {STATUS[status] || status}
    </span>
  );
}

export default function PortalDashboard() {
  const { user, loading, logout } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user?.role === "patient") api.get("/portal/me").then((res) => setData(res.data)).catch(() => setData({ error: true }));
  }, [user]);

  if (loading) return <main className="min-h-[60vh] bg-ivory px-6 pt-24 text-center text-moss">Loading...</main>;
  if (!user) return <Navigate to="/portal/login" replace />;
  if (user.role !== "patient") return <Navigate to="/admin" replace />;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (data?.bookings || []).filter((b) => b.date >= today && b.status !== "cancelled");
  const past = (data?.bookings || []).filter((b) => b.date < today || b.status === "cancelled");
  const history = [
    ...past.map((b) => ({ id: b.id, title: b.service_name, date: b.date, detail: `${formatTime12(b.time)} · ${b.visit_type_label || ""}`, status: b.status })),
    ...(data?.purchases || []).map((p) => ({ id: p.id, title: p.item_name, date: p.date, detail: p.notes, price: p.price })),
  ].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <main data-testid="portal-dashboard" className="bg-ivory px-6 pb-28 pt-16 sm:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">Patient Portal</p>
            <h1 data-testid="portal-greeting" className="mt-4 font-serif text-4xl font-medium leading-tight text-forest sm:text-5xl">Hello, {user.name.split(" ")[0]}</h1>
            <p className="mt-2 text-sm text-moss">{user.email}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/book" data-testid="portal-book-button" className="rounded-md bg-forest px-5 py-2.5 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark">Book a Visit</Link>
            <button data-testid="portal-logout-button" onClick={logout} className="flex items-center gap-2 rounded-md border border-linen px-5 py-2.5 text-sm font-semibold text-forest transition-colors hover:border-forest"><LogOut size={15} /> Sign Out</button>
          </div>
        </div>

        {!data && <p className="mt-12 text-sm text-moss">Loading your records...</p>}
        {data?.error && <p className="mt-12 text-sm text-destructive">We couldn't load your records. Please refresh.</p>}

        {data && !data.error && (
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <Section icon={CalendarDays} title="Upcoming visits" count={upcoming.length} testid="portal-upcoming">
              {upcoming.length === 0 ? <Empty>No upcoming visits. <Link to="/book" className="font-semibold text-forest hover:underline">Book one now.</Link></Empty> : (
                <ul className="space-y-3">
                  {upcoming.map((b) => (
                    <li key={b.id} data-testid={`portal-booking-${b.id}`} className="flex items-start justify-between gap-4 border-b border-linen pb-3 last:border-0 last:pb-0">
                      <div>
                        <p className="text-sm font-semibold text-forest">{b.service_name}</p>
                        <p className="mt-0.5 text-xs text-moss">{b.date} at {formatTime12(b.time)} &middot; {b.visit_type_label}</p>
                        <Link to={`/manage/${b.id}`} className="mt-1 inline-block text-xs font-semibold text-forest hover:underline">Manage / reschedule</Link>
                      </div>
                      <Pill status={b.status} />
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section icon={CreditCard} title="Memberships" count={data.memberships.length} testid="portal-memberships">
              {data.memberships.length === 0 && data.membership_requests.length === 0 ? (
                <Empty>No membership yet. <Link to="/#memberships" className="font-semibold text-forest hover:underline">See our plans.</Link></Empty>
              ) : (
                <ul className="space-y-3">
                  {data.memberships.map((m) => (
                    <li key={m.id} className="flex items-start justify-between gap-4 border-b border-linen pb-3 last:border-0 last:pb-0">
                      <div>
                        <p className="text-sm font-semibold text-forest">{m.plan_name}</p>
                        <p className="mt-0.5 text-xs text-moss">${m.price}/{m.interval} &middot; since {m.started_at.slice(0, 10)}</p>
                      </div>
                      <Pill status={m.status} />
                    </li>
                  ))}
                  {data.membership_requests.map((r) => (
                    <li key={r.id} className="flex items-start justify-between gap-4 border-b border-linen pb-3 last:border-0 last:pb-0">
                      <div>
                        <p className="text-sm font-semibold text-forest">{r.plan_name}</p>
                        <p className="mt-0.5 text-xs text-moss">Nichole will confirm and collect payment at your next visit.</p>
                      </div>
                      <Pill status="requested" />
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section icon={PiggyBank} title="Banked credit" count={0} testid="portal-credit">
              <div className="flex items-end justify-between gap-4 rounded-md bg-forest px-5 py-4 text-ivory">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-ivory/70">Available balance</p>
                  <p className="mt-1 text-xs text-ivory/70">Unused membership months bank here for future services.</p>
                </div>
                <p data-testid="portal-credit-balance" className="font-serif text-3xl">${data.credit_balance.toFixed(2)}</p>
              </div>
              {data.credits.length === 0 ? <p className="mt-4 text-sm text-moss">Credit from your membership will appear here each month.</p> : (
                <ul className="mt-4 space-y-2">
                  {data.credits.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-4 border-b border-linen pb-2 text-sm last:border-0 last:pb-0">
                      <span className="text-forest">{c.note} <span className="text-xs text-moss">&middot; {c.date}</span></span>
                      <span className={`font-semibold ${c.kind === "credit" ? "text-forest" : "text-destructive"}`}>{c.kind === "credit" ? "+" : "−"}${c.amount.toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section icon={History} title="Service history" count={history.length} testid="portal-service-history">
              {history.length === 0 ? <Empty>Every treatment you receive with us will be listed here.</Empty> : (
                <ul className="space-y-3">
                  {history.map((h) => (
                    <li key={h.id} className="flex items-start justify-between gap-4 border-b border-linen pb-3 last:border-0 last:pb-0">
                      <div>
                        <p className="text-sm font-semibold text-forest">{h.title}</p>
                        <p className="mt-0.5 text-xs text-moss">{h.date}{h.detail ? ` · ${h.detail}` : ""}</p>
                      </div>
                      {h.price != null ? <span className="font-serif text-lg text-forest">${h.price}</span> : <Pill status={h.status} />}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section icon={StickyNote} title="Progress notes" count={data.notes.length} testid="portal-notes">
              {data.notes.length === 0 ? <Empty>Notes from Nichole about your progress will appear here.</Empty> : (
                <ul className="space-y-4">
                  {data.notes.map((n) => (
                    <li key={n.id} className="border-b border-linen pb-4 last:border-0 last:pb-0">
                      <p className="text-sm leading-relaxed text-forest">{n.text}</p>
                      <p className="mt-1.5 text-xs text-moss">{n.author} &middot; {n.created_at.slice(0, 10)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section icon={FileText} title="Signed documents" count={data.forms.length} testid="portal-forms">
              {data.forms.length === 0 ? <Empty>Forms you complete online will be listed here.</Empty> : (
                <ul className="space-y-3">
                  {data.forms.map((f) => (
                    <li key={f.id} className="flex items-start justify-between gap-4 border-b border-linen pb-3 last:border-0 last:pb-0">
                      <div>
                        <p className="text-sm font-semibold text-forest">{f.form_name}</p>
                        <p className="mt-0.5 text-xs text-moss">Signed {f.submitted_at.slice(0, 10)} by {f.signature}{f.booking_service ? ` · ${f.booking_service}` : ""}</p>
                      </div>
                      <Pill status={f.status === "reviewed" ? "completed" : "booked"} />
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section icon={Gift} title="Gift certificates" count={data.gift_certificates.length} testid="portal-history">
              {data.gift_certificates.length === 0 ? <Empty>Gift certificates you purchase will appear here.</Empty> : (
                <ul className="space-y-3">
                  {data.gift_certificates.map((g) => (
                    <li key={g.id} className="flex items-start justify-between gap-4 border-b border-linen pb-3 last:border-0 last:pb-0">
                      <div>
                        <p className="text-sm font-semibold text-forest">Gift for {g.recipient_name} — {g.value_label}</p>
                        <Link to={`/gift/${g.id}`} className="mt-0.5 inline-block text-xs font-semibold text-forest hover:underline">View certificate {g.code}</Link>
                      </div>
                      <Pill status={g.status} />
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        )}
      </div>
    </main>
  );
}
